import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { Button, Carte, Grille, Infobulle } from "@/shared/ui";
import { Image as ImageIcon, Loader2 } from "@/shared/ui/icons";
import {
  DEFAULT_WALLPAPER,
  imageFileToWallpaper,
  prechargerLesFonds,
  wallpaperAtom,
  WALLPAPER_PRESETS,
  type Wallpaper,
} from "@/shell/wallpaperState";

/**
 * Sélecteur de fond d'écran du bureau — la collection d'images du cockpit,
 * le « Thème » (aucun fond) et l'import d'une image à soi (redimensionnée
 * côté client en data-URL, persistée dans `wallpaperAtom`). Présentationnel
 * et SANS wrapper de section : réutilisé tel quel par l'accueil du chatteur
 * et la modale « Personnaliser » (cf `AtelierDuTheme`).
 *
 * Sélection = la `Carte` choisie (`actif` : filet et halo à la marque) — jamais
 * de fill teinté (cf CLAUDE.md).
 */
export function WallpaperPicker() {
  const { t } = useTranslation();
  const [wp, setWp] = useAtom(wallpaperAtom);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ⚡ Les images pleines se téléchargent dès que la grille s'affiche.
  useEffect(() => prechargerLesFonds(), []);

  async function handlePick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError(t("shell.settings.uploader.notImage"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      setWp(await imageFileToWallpaper(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const customActive = wp.id === "custom" && wp.kind === "image";
  // Un dégradé d'avant le 07-10-2026 s'affiche comme le thème : c'est ce que
  // l'écran rend (cf `wallpaperBackground`), la grille dit donc la même chose.
  const idCourant = wp.kind === "image" ? wp.id : DEFAULT_WALLPAPER.id;

  return (
    <div className="flex flex-col gap-3">
      <Grille min={88} ecart={8}>
        {WALLPAPER_PRESETS.map((p) => (
          <WallpaperSwatch
            key={p.id}
            wp={p}
            selected={idCourant === p.id}
            label={p.labelKey ? t(p.labelKey) : p.nom ?? ""}
            onPick={() => setWp(p)}
          />
        ))}
        {customActive && (
          <WallpaperSwatch
            wp={wp}
            selected
            label={t("shell.settings.preferences.wallpaper.custom")}
            onPick={() => {}}
          />
        )}
      </Grille>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="lg"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          icon={
            busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ImageIcon className="h-4 w-4" />
            )
          }
        >
          {t("shell.settings.preferences.wallpaper.import")}
        </Button>
        {customActive && (
          <Button variant="ghost" size="lg" onClick={() => setWp(DEFAULT_WALLPAPER)}>
            {t("shell.settings.preferences.wallpaper.removeImage")}
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handlePick}
          className="hidden"
        />
      </div>
      {error && (
        <p className="text-[12px] text-erreur" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function WallpaperSwatch({
  wp,
  selected,
  label,
  onPick,
}: {
  wp: Wallpaper;
  selected: boolean;
  label: string;
  onPick: () => void;
}) {
  const image = wp.kind === "image" ? wp.vignette ?? wp.value : null;
  const carte = (
    <Carte
      remplissage="aucun"
      actif={selected}
      aria-pressed={selected}
      aria-label={label || wp.id}
      onClick={onPick}
      className="aspect-[16/10] overflow-hidden"
    >
      {image ? (
        <img
          src={image}
          alt=""
          loading="lazy"
          draggable={false}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-[12px] font-medium text-muted">
          {label || "Aa"}
        </span>
      )}
    </Carte>
  );
  return label ? <Infobulle texte={label}>{carte}</Infobulle> : carte;
}
